/**
 * @taj/types — Contrats partagés front ⇄ back.
 * Source unique de vérité pour les DTO REST et les événements Socket.IO.
 */

// ─────────── Enums métier (alignés sur le schéma Prisma) ───────────
export type StaffRole = 'ADMIN' | 'RESPONSABLE' | 'CAISSIER';
export type DeviceRole = 'KIOSK' | 'CASHIER' | 'KDS' | 'DISPLAY' | 'BACKOFFICE' | 'WEB' | 'PICKUP';
export type OrderChannel = 'KIOSK' | 'CASHIER' | 'ONLINE' | 'UBER_EATS' | 'DELIVEROO';
export type OrderType = 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY' | 'CLICK_COLLECT';
export type OrderStatus =
  | 'CART'
  | 'PENDING_PAYMENT'
  | 'RECEIVED'
  | 'PREPARING'
  | 'READY'
  | 'COMPLETED'
  | 'CANCELLED';
export type LineStatus = 'PENDING' | 'PREPARING' | 'READY' | 'SERVED' | 'CANCELLED';
export type PaymentMethod =
  | 'CASH'
  | 'CARD'
  | 'TICKET_RESTO'
  | 'CARTE_TICKET'
  | 'MEAL_VOUCHER'
  | 'ONLINE';
export type PaymentStatus = 'PENDING' | 'CAPTURED' | 'REFUNDED' | 'PARTIALLY_REFUNDED' | 'VOID';
export type DiscountType = 'PERCENT' | 'AMOUNT';

// ─────────── Permissions (cases cochables) ───────────
export interface PermissionSet {
  caisse: boolean;
  cuisine: boolean;
  backOffice: boolean;
  canCancel: boolean;
  canRefund: boolean;
  canClose: boolean;
}

// ─────────── Auth ───────────
export interface LoginPinDto {
  pin: string;
}
export interface LoginAdminDto {
  fullName: string;
  password: string;
}
export interface AuthResult {
  token: string;
  staff: { id: string; fullName: string; role: StaffRole; permissions: PermissionSet };
}
export interface StaffRow {
  id: string;
  fullName: string;
  role: StaffRole;
  active: boolean;
  hasPin: boolean;
  hasPassword: boolean;
  permissions: PermissionSet;
  loginToken?: string | null; // jeton du QR de connexion (back-office uniquement)
}

// ─────────── Messages clients (SMS Twilio) ───────────
export interface MessagingCustomer {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  points: number;
}
export interface MessagingSendResult {
  total: number;
  sent: number;
  simulated: number;
  failed: number;
  mediaUrl: string | null;
}
export interface CampaignRow {
  id: string;
  name: string;
  message: string;
  subject: string;
  channel: string; // sms | email | both
  scheduledAt: string;
  recurrence: string; // once | weekly | monthly
  active: boolean;
  lastSentAt: string | null;
}

// ─────────── Fidélité ───────────
export interface LoyaltyMember {
  name?: string | null;
  phone: string;
  points: number;
  loyaltyCode: string;
  isNew: boolean;
  pointsEarned: number;
}

// ─────────── Rapports / stats ───────────
export interface ReportSummary {
  period: 'day' | 'week';
  from: string;
  to: string;
  revenueCents: number;
  taxCents: number;
  htCents: number;
  orderCount: number;
  itemsSold: number;
  avgBasketCents: number;
  byMethod: Record<string, number>;
  byType: Record<string, number>;
  byChannel: Record<string, number>;
  partners: { partnerId: string; type: string; grossCents: number; commissionCents: number; netCents: number; count: number }[];
  daily: { date: string; revenueCents: number; itemsSold: number }[];
  bestDay: { date: string; revenueCents: number } | null;
  topProducts: { name: string; emoji: string | null; qty: number; revenueCents: number }[];
  unsoldProducts: { name: string; emoji: string | null }[];
}

export interface RecentOrder {
  number: number;
  clientName?: string | null;
  totalCents: number;
  status: OrderStatus;
  channel: OrderChannel;
  type: OrderType;
  createdAt: string;
  paymentIntent?: PaymentMethod | null;
}

// ─────────── Imprimantes ───────────
export interface StationPrinter {
  stationId: string;
  stationName: string;
  printer: { ip: string; port: number; active: boolean } | null;
  removable?: boolean; // true = poste ajouté (Caisse/Borne/extra), supprimable
}

// ─────────── Terminaux de paiement (SumUp Solo) ───────────
/** Un lecteur de carte appairé à un poste (« Borne 1 », « Caisse »…). */
export interface CardReaderRow {
  station: string;
  name: string;
  readerId: string;
  model?: string | null; // solo | virtual-solo (terminal de test)
  active: boolean;
  /** État remonté par SumUp : paired = prêt, expired = à ré-appairer. */
  status?: 'unknown' | 'processing' | 'paired' | 'expired' | 'offline';
}

export type TerminalPaymentState = 'PENDING' | 'PAID' | 'FAILED' | 'CANCELLED';

/** Suivi d'un paiement demandé à un terminal. Le poste interroge cet état jusqu'à l'issue. */
export interface TerminalPaymentDto {
  id: string;
  orderId: string;
  station: string;
  status: TerminalPaymentState;
  amountCents: number;
  message?: string | null;
  /** Renseigné quand le paiement a abouti (commande soldée, partie en cuisine). */
  order?: OrderDto | null;
}

/** Disponibilité du paiement par terminal, pour un poste donné. */
export interface TerminalAvailability {
  configured: boolean; // clés SumUp présentes sur le serveur
  reader: CardReaderRow | null; // lecteur rattaché à ce poste
  available: boolean; // configured && reader actif
}

// ─────────── Livraison ───────────
export interface DeliveryZoneTier {
  maxKm: number; // livrable jusqu'à cette distance (km)
  feeCents: number; // frais de livraison pour cette zone
  minOrderCents?: number; // minimum de commande propre à la zone (sinon global)
  freeAboveCents?: number | null; // gratuit au-dessus de ce montant (sinon global)
}
export interface DeliveryConfig {
  enabled: boolean;
  originLabel: string | null;
  originLat: number | null;
  originLon: number | null;
  feeCents: number;
  freeAboveCents: number | null;
  minOrderCents: number;
  maxKm: number;
  zones: DeliveryZoneTier[];
}
export interface DeliveryQuoteResult {
  enabled: boolean;
  eligible: boolean;
  distanceKm: number;
  reason: string | null;
  feeCents: number;
  freeAboveCents: number | null;
  minOrderCents: number;
  maxKm: number;
  zones: DeliveryZoneTier[];
  zoneLabel: string | null; // ex. "Zone 2 : 3–6 km"
}

// ─────────── Liste de courses (approvisionnement) ───────────
export interface ShoppingItem {
  id: string;
  name: string;
  category: string; // rayon / catégorie
  unit: string; // kg, L, pièces…
  quantity: string; // saisie libre
  checked: boolean; // à acheter
  position: number;
  productId?: string | null; // lien produit si ajouté auto (stock bas)
  auto?: boolean; // ajouté automatiquement
}
export interface CustomerPrefill {
  name: string | null;
  phone: string | null;
  address: string | null;
  lat: number | null;
  lon: number | null;
  points: number;
}

// ─────────── Réglages restaurant ───────────
export interface RestaurantInfo {
  name: string;
  tagline: string;
  address: string;
  phone: string;
  web: string;
  /** Horaires : 7 entrées, index 0 = Lundi … 6 = Dimanche. '' = fermé ce jour-là. */
  hours?: string[];
  instagram?: string;
  facebook?: string;
  tiktok?: string;
  onlinePayCash?: boolean;
  onlinePayCard?: boolean;
  onlineOrderingOpen?: boolean;
  /** Borne : proposer l'option « Sur place » (sinon seulement « À emporter »). */
  kioskDineIn?: boolean;
  /** Commandes demandées pour plus tard : 'NOW' = cuisine tout de suite · 'AUTO' = retenue puis envoyée avant l'heure. */
  scheduledMode?: 'NOW' | 'AUTO';
  /** En mode AUTO : combien de minutes avant l'heure voulue la commande part en cuisine. */
  scheduledLeadMin?: number;
}

// ─────────── Catalogue ───────────
export interface OptionDto {
  id: string;
  name: string;
  priceDelta: number; // centimes
  allergens: string[];
  imageUrl?: string | null;
}
export interface OptionGroupDto {
  id: string;
  name: string;
  minSelect: number;
  maxSelect: number;
  options: OptionDto[];
}
export interface ProductDto {
  id: string;
  categoryId: string;
  name: string;
  nameEn?: string | null;
  description?: string | null;
  descriptionEn?: string | null;
  emoji?: string | null;
  photoUrl?: string | null;
  priceCents: number;
  vatRate: number;
  available: boolean;
  badges: string[];
  allergens: string[];
  optionGroups: OptionGroupDto[];
  stock?: { quantity: number; trackStock: boolean; threshold: number; reorderQty: string; reorderUnit: string; portionsPerBatch: number } | null;
}
export interface CategoryDto {
  id: string;
  name: string;
  nameEn?: string | null;
  emoji?: string | null;
  imageUrl?: string | null;
  position: number;
  stationId?: string | null;
  products: ProductDto[];
}

// ─────────── Commandes ───────────
export interface CartLineInput {
  productId: string;
  qty: number;
  optionIds?: string[];
  notes?: string;
}
export interface CreateOrderDto {
  channel: OrderChannel;
  type: OrderType;
  clientName?: string;
  customerId?: string;
  lines: CartLineInput[];
  paymentMethod?: PaymentMethod; // intention de paiement à la borne
  loyaltyPhone?: string; // téléphone OU code carte fidélité → remise membre + points
  customerPhone?: string; // téléphone client (mémorisé pour retrouver au prochain passage)
  customerEmail?: string; // email client → reçoit le lien + QR de suivi (commande reçue, puis prête)
  discountType?: 'PERCENT' | 'AMOUNT'; // remise manuelle saisie en caisse (POS)
  discountValue?: number; // % (1-100) ou centimes
  sendToKitchen?: boolean; // envoyer en cuisine sans paiement (ex. livraison payée à la livraison)
  // Paiement par carte sur un TERMINAL (SumUp) : la commande n'est ni marquée payée ni
  // envoyée en cuisine tant que le terminal n'a pas accepté la carte. Sans ce drapeau,
  // « Carte » vaut encaissement immédiat (TPE saisi à la main par le caissier).
  awaitTerminal?: boolean;
  /** Heure demandée par le client, format « HH:MM » (site). Vide = dès que possible. */
  wantedTime?: string;
  // Livraison (type DELIVERY)
  deliveryPhone?: string;
  deliveryAddress?: string;
  deliveryLat?: number;
  deliveryLon?: number;
  deliveryFeeCentsOverride?: number; // caisse : prix de livraison fixé à la main (ignore le calcul par zone)
  partnerId?: string; // commande passée pour un partenaire (Uber Eats, Deliveroo…) saisie en caisse
}

// ─────────── Fidélité : recherche d'un membre ───────────
export interface LoyaltyLookup {
  member: LoyaltyMember | null;
  discountPct: number; // remise appliquée aux membres (config)
}
export interface LoyaltyReward {
  points: number; // points nécessaires
  label: string; // récompense (ex. "Boisson offerte")
}
export interface LoyaltyConfigDto {
  enabled: boolean; // programme fidélité actif ou non
  pointsPerEuro: number;
  signupDiscount: number; // remise % à l'inscription
  rewards: LoyaltyReward[];
}
export interface LoyaltyMemberRow {
  name: string | null;
  phone: string | null;
  loyaltyCode: string | null;
  points: number;
  email: string | null;
  createdAt: string;
}

// ─────────── Promotions / remises ───────────
export type PromoKind = 'PERCENT' | 'AMOUNT';
export interface PromoItem {
  id: string;
  name: string;
  kind: PromoKind;
  value: number; // pourcentage si PERCENT, centimes si AMOUNT
}
export interface PromoConfigDto {
  enabled: boolean;
  promos: PromoItem[];
}

// ─────────── Partenaires (Uber Eats / Deliveroo…) ───────────
export interface PartnerRow {
  id: string;
  type: string; // nom du partenaire (ex. "Uber Eats")
  commissionPct: number;
  active: boolean;
}

// ─────────── Audit (journal NF525) ───────────
export interface AuditRow {
  id: string;
  action: string;
  staffName: string | null;
  targetId: string | null;
  detail: string | null;
  createdAt: string;
}

// ─────────── Clôture de caisse (Ticket Z) ───────────
/** Détail de fin de service imprimé sur le Ticket Z. */
export interface ZDetail {
  /** Ce qui a été vendu, regroupé par catégorie (plats, boissons, desserts…). */
  byCategory: {
    category: string;
    qty: number;
    revenueCents: number;
    products: { name: string; qty: number; revenueCents: number }[];
  }[];
  /** Commandes annulées pendant le service (avec qui l'a fait). */
  cancelled: { number: number; token: string | null; type: string; clientName: string | null; totalCents: number; at: string; by: string | null }[];
  /** Paiements annulés / modifiés pendant le service (avec qui l'a fait). */
  voids: { orderNumber: number; orderToken: string | null; method: string; amountCents: number; at: string; by: string | null }[];
  /** Stock restant en fin de service (produits suivis uniquement). */
  stock: { name: string; category: string; quantity: number; threshold: number; low: boolean }[];
  /** Ce que chaque employé a encaissé / annulé pendant le service. */
  byStaff: {
    name: string;
    collectedCents: number; // total encaissé
    paymentCount: number; // nombre d'encaissements
    voidedCents: number; // total des paiements annulés
    voidCount: number; // nombre d'annulations
  }[];
  /** Panier moyen et volume du service. */
  avgBasketCents: number;
  itemsSold: number;
  /** Contrôle des espèces : fond de caisse d'ouverture + espèces encaissées = attendu en tiroir. */
  openingFloatCents: number;
  cashCollectedCents: number;
  cashExpectedCents: number;
}

export interface ZClosureRow {
  id: string;
  number: number;
  fiscalSeq: number;
  from: string;
  to: string;
  revenueCents: number;
  htCents: number;
  taxCents: number;
  orderCount: number;
  byMethod: Record<string, number>;
  byType: Record<string, number>;
  detail?: ZDetail;
  openedByName?: string | null;
  openedAt?: string | null;
  closedByName?: string | null;
  createdAt: string;
}

/** Ouverture de caisse en cours (qui a pris le service). */
export interface CashSessionRow {
  id: string;
  openedByName: string;
  openedAt: string;
  openingFloatCents: number; // fond de caisse (monnaie de départ)
}

/** Commande encore ouverte : empêche la clôture tant qu'elle n'est pas terminée. */
export interface OpenOrderRow {
  number: number;
  token: string | null;
  status: OrderStatus;
  type: string;
  clientName: string | null;
  totalCents: number;
  paid: boolean;
}

/** Aperçu avant clôture : les totaux du jour + le détail de fin de service. */
export interface ZPreview extends ReportSummary {
  detail: ZDetail;
  session: CashSessionRow | null;
  /** Tant que cette liste n'est pas vide, la clôture est refusée. */
  openOrders: OpenOrderRow[];
}
export interface OrderLineDto {
  id: string;
  productId: string;
  nameSnap: string;
  unitPriceCents: number;
  vatRate: number;
  qty: number;
  status: LineStatus;
  station?: string | null;
  notes?: string | null;
  options: { id: string; optionId: string; nameSnap: string; priceDelta: number }[];
}
export interface OrderDto {
  id: string;
  number: number;
  token?: string | null;
  channel: OrderChannel;
  type: OrderType;
  status: OrderStatus;
  paymentIntent?: PaymentMethod | null;
  clientName?: string | null;
  /** Téléphone du client (commande du site, livraison) — affiché en caisse pour pouvoir le joindre. */
  clientPhone?: string | null;
  clientEmail?: string | null;
  deliveryAddress?: string | null;
  deliveryFeeCents?: number;
  /** Heure demandée par le client (« pour 20h »). Null = dès que possible. */
  wantedAt?: string | null;
  /** true = commande retenue, elle partira en cuisine peu avant l'heure voulue. */
  scheduledHold?: boolean;
  /** Le client du site a choisi « Carte » avec paiement en ligne possible : s'il n'a pas payé, c'est un abandon. */
  onlinePayExpected?: boolean;
  lines: OrderLineDto[];
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  paidCents: number;
  fiscalRef?: string | null; // code de traçabilité NF525 (séquence-signature)
  trackUrl?: string | null; // URL publique de suivi de la commande (si SITE_URL configuré)
  placedAt?: string | null;
  createdAt: string;
}

// ─────────── Paiements ───────────
export interface AddPaymentDto {
  orderId: string;
  method: PaymentMethod;
  amountCents: number; // permet le paiement mixte (plusieurs appels)
  lineIds?: string[]; // paiement par article
}

// ─────────── Réservations de table ───────────
export type ReservationStatus = 'PENDING' | 'CONFIRMED' | 'DECLINED' | 'CANCELLED';

/** Réservation envoyée par le client depuis le site. */
export interface CreateReservationDto {
  name: string;
  phone: string;
  email?: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  partySize: number;
  note?: string;
}

/** Réservation telle qu'affichée en caisse. */
export interface ReservationRow {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  date: string;
  time: string;
  partySize: number;
  note: string | null;
  status: ReservationStatus;
  declineReason: string | null;
  channel: string;
  createdAt: string;
}

// ─────────── Événements Socket.IO (typés bout-en-bout) ───────────
export interface ServerToClientEvents {
  'order:new': (order: OrderDto) => void;
  'order:status': (payload: { orderId: string; status: OrderStatus; order: OrderDto }) => void;
  'reservation:new': (reservation: ReservationRow) => void;
  'line:status': (payload: { orderId: string; lineId: string; status: LineStatus }) => void;
  'stock:update': (payload: { productId: string; quantity: number }) => void;
  'catalog:update': () => void;
  'notification': (payload: { level: 'info' | 'warn' | 'critical'; message: string }) => void;
}
export interface ClientToServerEvents {
  'join': (room: SocketRoom) => void;
  'leave': (room: SocketRoom) => void;
}

/** Rooms : un appareil rejoint sa room selon son rôle / poste. */
export type SocketRoom =
  | 'cashier'
  | 'display'
  | 'backoffice'
  | `kds:${string}`; // ex: "kds:Thaï"

export const ROOMS = {
  cashier: 'cashier' as const,
  display: 'display' as const,
  backoffice: 'backoffice' as const,
  kds: (station: string): SocketRoom => `kds:${station}`,
};
